import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useProfile";
import { commissionsApi, type ReportFilters } from "@/lib/commissions/api";
import type { GroupKind } from "@/lib/commissions/groups";
import { toItemProfessionalMap } from "@/lib/commissions/item-professionals";

function useClinicId() {
  const { data: profile } = useProfile();
  return profile?.current_clinic_id ?? null;
}

export function useCatalogGroups(enabled = true) {
  const clinicId = useClinicId();
  return useQuery({ queryKey: ["commissions", clinicId, "groups"], queryFn: commissionsApi.catalogGroups, enabled: enabled && !!clinicId });
}
export function useCatalogItems(enabled = true) {
  const clinicId = useClinicId();
  return useQuery({ queryKey: ["commissions", clinicId, "items"], queryFn: commissionsApi.catalogItems, enabled: enabled && !!clinicId });
}
export function useMemberModels(enabled = true) {
  const clinicId = useClinicId();
  return useQuery({ queryKey: ["commissions", clinicId, "member-models"], queryFn: commissionsApi.memberModels, enabled: enabled && !!clinicId });
}
export function useCommissionModels(enabled = true) {
  const clinicId = useClinicId();
  return useQuery({ queryKey: ["commissions", clinicId, "models"], queryFn: commissionsApi.listModels, enabled: enabled && !!clinicId });
}
export function useMemberCommission(userId: string | undefined, enabled = true) {
  const clinicId = useClinicId();
  return useQuery({
    queryKey: ["commissions", clinicId, "member", userId],
    queryFn: () => commissionsApi.memberSetup(userId!),
    enabled: enabled && !!clinicId && !!userId,
  });
}
export function useCommissionProfessionals() {
  const clinicId = useClinicId();
  return useQuery({ queryKey: ["commissions", clinicId, "professionals"], queryFn: commissionsApi.professionals, enabled: !!clinicId });
}
/** Quem ganha comissão em cada item; o PDV usa para mostrar o select "Profissional" só quando há alguém. */
export function useCommissionItemProfessionals() {
  const clinicId = useClinicId();
  return useQuery({
    queryKey: ["commissions", clinicId, "item-professionals"],
    queryFn: commissionsApi.itemProfessionals,
    enabled: !!clinicId,
    select: toItemProfessionalMap,
  });
}
export function useCommissionReport(filters: ReportFilters) {
  const clinicId = useClinicId();
  return useQuery({ queryKey: ["commissions", clinicId, "report", filters], queryFn: () => commissionsApi.report(filters), enabled: !!clinicId });
}

function useCommissionMutation<TVars, TResult = void>(fn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  const clinicId = useClinicId();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["commissions", clinicId] });
    },
  });
}

export const useSaveModel = () =>
  useCommissionMutation((m: { id: string | null; name: string; basis: "gross" | "after_discount"; isActive: boolean }) => commissionsApi.saveModel(m));
export const useSetRate = () =>
  useCommissionMutation((v: { modelId: string; kind: GroupKind; groupId: string; percent: number | null }) =>
    commissionsApi.setRate(v.modelId, v.kind, v.groupId, v.percent));
export const useSetMemberModel = () =>
  useCommissionMutation((v: { userId: string; modelId: string | null }) => commissionsApi.setMemberModel(v.userId, v.modelId));
export const useSetOverride = () =>
  useCommissionMutation((v: { userId: string; kind: GroupKind; groupId: string; percent: number | null }) =>
    commissionsApi.setOverride(v.userId, v.kind, v.groupId, v.percent));
export const useSetItemRate = () =>
  useCommissionMutation((v: { modelId: string; itemId: string; percent: number | null }) =>
    commissionsApi.setItemRate(v.modelId, v.itemId, v.percent));
export const useClearGroupItemRates = () =>
  useCommissionMutation((v: { modelId: string; kind: GroupKind; groupId: string }) =>
    commissionsApi.clearGroupItemRates(v.modelId, v.kind, v.groupId));
export const useSetItemOverride = () =>
  useCommissionMutation((v: { userId: string; itemId: string; percent: number | null }) =>
    commissionsApi.setItemOverride(v.userId, v.itemId, v.percent));
export const useSetModelMembers = () =>
  useCommissionMutation((v: { modelId: string; userIds: string[] }) => commissionsApi.setModelMembers(v.modelId, v.userIds));
